<script setup lang="ts">
import { watch, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

import { ResourceResponse, uuid } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import useForm from '@/hooks/use-form'
import { Tour } from '@/types/delivery-management'
import { emitter } from '@/utils/mitt'

import MyModal from '@/components/my-components/MyModal.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'

interface Props {
    modelValue: boolean
    tourId: uuid
}

const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void; (e: 'saved'): void }>()

const { t } = useI18n()
const auth = useAuthStore()

const { data, submit, errors, loading } = useForm<{ placement: string }>({
    placement: '',
})

async function fetchTour() {
    const res = await axios.get<ResourceResponse<Tour>>(
        window.route('dm.company.tours.show', {
            company: auth.companyId,
            tour: props.tourId,
        }),
    )

    data.placement = res.data.data.placement ?? ''
}

async function onSubmit() {
    const url = window.route('dm.company.tours.update-placement', {
        company: auth.companyId,
        tour: props.tourId,
    })
    const response = await submit('PUT', url)

    if (response !== undefined) {
        emitter.emit('fetchTours')
        emit('update:modelValue', false)
        emit('saved')
    }
}

function onOpen() {
    if (!props.modelValue) return
    fetchTour()
}

onBeforeMount(onOpen)
watch(() => props.modelValue, onOpen)
</script>

<template>
    <MyModal :value="props.modelValue" @close="emit('update:modelValue', false)">
        <template #title>{{ t('assignPlacement') }}</template>

        <LoaderWrapper :visible="loading" immediate />

        <MyForm :errors="errors" @submit.prevent="onSubmit">
            <MyInput v-model="data.placement" autofocus :label="t('placement')" name="placement" />

            <div class="mt-4 flex justify-end">
                <MyButton scheme="primary" :disabled="loading" v-text="t('save')" />
            </div>
        </MyForm>
    </MyModal>
</template>
